import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Select
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import StyledDatePicker from '../date&time/StyledDatePicker';
import FileUploadCard from '../FileUploadCard';
import NumericInput from '../NumericInput';
import {
  CampaignListKind,
  CampaignType,
  campaignTypesFor
} from '@/enums/campaign-type';
import { companyService } from '@/data/companyService';
import { branchService } from '@/data/branchService';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { Campaign } from '@/types/Campaign.interface';
import { CampaignStatus } from '@/enums/campaign-status';
import { Company } from '@/types/Company.interface';
import { Branch } from '@/types/Branch.interface';
import { categoryService } from '@/data/categoryService';
import { Category } from '@/types/Category.interface';
import { LEGAL_VAT_RATES } from '@/types/DailyLoginType.interface';
import { fetchImageAsFile } from '@/utils/imageFile';

type CampaignDialogProps = {
  open: boolean;
  onClose: () => void;
  error?: string;
  campaign?: Campaign | null;
  /**
   * Copy mode: a new campaign prefilled from this one ("(Kopya)" title, dates
   * moved to start now with the same length, image copied). Ignored when
   * `campaign` is set.
   */
  template?: Campaign | null;
  /** 'package': tür sabit Etkinlik Paketi, indirim ve tür alanı gizli. */
  kind?: CampaignListKind;
  onSave: (campaign: {
    title: string;
    description: string;
    discount: number;
    startDateTime: Date;
    endDateTime: Date;
    type: CampaignType;
    imageFile?: File | null;
    branchId: number;
    status?: CampaignStatus;
    bundlePrice?: number;
    bundleTotalCount?: number;
    bundleVatRate?: number | null;
    categoryId?: number;
  }) => Promise<void>;
};

const CampaignDialog = ({
  open,
  onClose,
  onSave,
  error,
  campaign,
  template,
  kind = 'campaign'
}: CampaignDialogProps) => {
  const { t } = useTranslation();
  const {
    isSuperAdmin,
    isCompanyAdmin,
    isBranchAdmin,
    isAdminView,
    company,
    currentBranch
  } = useUserViewMode();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [discount, setDiscount] = useState<number>(0);
  const [startDateTime, setStartDateTime] = useState(new Date());
  const [endDateTime, setEndDateTime] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [campaignType, setCampaignType] = useState<CampaignType | ''>('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('');
  const [selectedBranchId, setSelectedBranchId] = useState<number | ''>('');
  const [fetchingCompanies, setFetchingCompanies] = useState(false);
  const [fetchingBranches, setFetchingBranches] = useState(false);
  const branchesRequest = useRef(0);
  const [bundlePrice, setBundlePrice] = useState<number | ''>('');
  const [bundleTotalCount, setBundleTotalCount] = useState<number | ''>('');
  const [bundleVatRate, setBundleVatRate] = useState<number | null>(null);
  const [status, setStatus] = useState<CampaignStatus | ''>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | ''>('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [fetchingCategories, setFetchingCategories] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [copyImage, setCopyImage] = useState<'loading' | 'failed' | null>(null);
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined
  );
  const isEditMode = !!campaign;
  const isPackageKind = kind === 'package';
  // Düzenlenen kaydın türü bu sayfanın listesinde yoksa (eski veri) yine de
  // seçili görünsün.
  const typeOptions =
    campaign?.type && !campaignTypesFor(kind).includes(campaign.type)
      ? [...campaignTypesFor(kind), campaign.type]
      : campaignTypesFor(kind);
  const isBundleType =
    campaignType === CampaignType.BUNDLE_PRODUCT ||
    campaignType === CampaignType.BUNDLE_ACTIVITY;
  const isBundleActivity = campaignType === CampaignType.BUNDLE_ACTIVITY;
  // Şube görünümünde kampanyanın şubesi üstteki şube seçiminden gelir; şirket
  // de o şubenin şirketi olmalı. Aksi halde şirket boş kalıyor ve seçilen
  // şirket, kaydedilen şubeyle uyuşmayabiliyordu.
  const branchViewCompanyId =
    !isAdminView && currentBranch
      ? (currentBranch.companyId ?? currentBranch.company?.id)
      : undefined;

  useEffect(() => {
    if (open) {
      if (isEditMode && campaign) {
        // Populate form with campaign data
        setTitle(campaign.title || '');
        setDescription(campaign.description || '');
        setDiscount(campaign.discount || 0);
        setStartDateTime(new Date(campaign.startDateTime));
        setEndDateTime(
          campaign.endDateTime ? new Date(campaign.endDateTime) : new Date()
        );
        setCampaignType(campaign.type || '');
        setStatus(campaign.status || '');
        setCurrentImageUrl(campaign.file?.url || null);

        if (
          campaign.campaignCategories &&
          campaign.campaignCategories.length > 0
        ) {
          setSelectedCategoryId(campaign.campaignCategories[0].categoryId);
        }

        if (campaign.campaignBundle?.bundle) {
          setBundlePrice(campaign.campaignBundle.bundle.price || '');
          setBundleTotalCount(campaign.campaignBundle.bundle.totalCount || '');
          setBundleVatRate(campaign.campaignBundle.bundle.vatRate ?? null);
        }

        if (campaign.branch) {
          setSelectedBranchId(campaign.branch.id);
          if (campaign.branch.company && campaign.branch.company.id) {
            setSelectedCompanyId(campaign.branch.company.id);
            fetchBranches(campaign.branch.company.id);
          }
        }
      } else {
        resetForm();
        // Paketler sayfasında tür sabit: Etkinlik Paketi.
        if (isPackageKind) setCampaignType(CampaignType.BUNDLE_ACTIVITY);
        if (isSuperAdmin && branchViewCompanyId) {
          setSelectedCompanyId(branchViewCompanyId);
        }
        if (template) applyTemplate(template);
      }

      if (isSuperAdmin) {
        fetchCompanies();
      } else if (isCompanyAdmin && company?.id) {
        fetchBranches(company.id);
      }
    }
    setValidationError(undefined);
    return () => {
      branchesRequest.current += 1;
    };
    // applyTemplate/fetch* are plain functions recreated every render; the
    // form must only reset when the dialog opens or its source changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    isEditMode,
    campaign,
    template,
    isSuperAdmin,
    isCompanyAdmin,
    company,
    isPackageKind,
    branchViewCompanyId
  ]);

  // Kopya: kaynağın alanları; tarihler bugünden aynı süreyle başlar.
  function applyTemplate(source: Campaign) {
    setTitle(`${source.title} ${t('copy.suffix')}`);
    setDescription(source.description || '');
    setDiscount(source.discount || 0);
    const start = new Date();
    setStartDateTime(start);
    const sourceStart = new Date(source.startDateTime).getTime();
    const sourceEnd = source.endDateTime
      ? new Date(source.endDateTime).getTime()
      : NaN;
    setEndDateTime(
      Number.isFinite(sourceEnd - sourceStart) && sourceEnd > sourceStart
        ? new Date(start.getTime() + (sourceEnd - sourceStart))
        : start
    );
    if (!isPackageKind) setCampaignType(source.type || '');
    setCurrentImageUrl(source.file?.url || null);
    if (source.campaignCategories?.length) {
      setSelectedCategoryId(source.campaignCategories[0].categoryId);
    }
    if (source.campaignBundle?.bundle) {
      setBundlePrice(source.campaignBundle.bundle.price || '');
      setBundleTotalCount(source.campaignBundle.bundle.totalCount || '');
      setBundleVatRate(source.campaignBundle.bundle.vatRate ?? null);
    }
    if (source.branch) {
      setSelectedBranchId(source.branch.id);
      if (source.branch.company?.id) {
        setSelectedCompanyId(source.branch.company.id);
        fetchBranches(source.branch.company.id);
      }
    }
  }

  // Yeni kampanyada görsel zorunlu: kaynağın görselini dosya olarak al.
  useEffect(() => {
    const url = template?.file?.url;
    if (!open || campaign || !url) return;
    let cancelled = false;
    setCopyImage('loading');
    fetchImageAsFile(url, template.title)
      .then((file) => {
        if (cancelled) return;
        setImageFile(file);
        setCopyImage(null);
      })
      .catch(() => {
        if (!cancelled) setCopyImage('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [open, campaign, template]);

  const fetchCompanies = async () => {
    try {
      setFetchingCompanies(true);
      const data = await companyService.getAll({});
      setCompanies(data.items || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setFetchingCompanies(false);
    }
  };

  const fetchBranches = async (companyId: number) => {
    const request = ++branchesRequest.current;
    try {
      setFetchingBranches(true);
      const result = await branchService.getAllFlat({ companyId });
      if (request !== branchesRequest.current) return;
      setBranches(result || []);
    } catch (error) {
      if (request !== branchesRequest.current) return;
      console.error('Error fetching branches:', error);
    } finally {
      if (request === branchesRequest.current) setFetchingBranches(false);
    }
  };

  const fetchCategories = async (companyId: number) => {
    try {
      setFetchingCategories(true);
      const result = await categoryService.getAllFlat({
        companyId,
        getAll: true
      });
      setCategories(result || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setFetchingCategories(false);
    }
  };

  useEffect(() => {
    if (!open || campaignType !== CampaignType.CATEGORY) {
      setCategories([]);
      setSelectedCategoryId('');
      return;
    }
    let companyId: number | undefined;
    if (isSuperAdmin) {
      companyId =
        typeof selectedCompanyId === 'number' ? selectedCompanyId : undefined;
    } else if (isCompanyAdmin) {
      companyId = company?.id;
    } else {
      companyId = currentBranch?.companyId;
    }
    if (companyId) {
      fetchCategories(companyId);
    } else {
      setCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignType, selectedCompanyId, open]);

  const handleCompanyChange = (companyId: number) => {
    branchesRequest.current += 1;
    setSelectedCompanyId(companyId);
    setSelectedBranchId('');
    setBranches([]);
    if (companyId) {
      fetchBranches(companyId);
    } else {
      setFetchingBranches(false);
    }
  };

  const resetForm = () => {
    branchesRequest.current += 1;
    setFetchingBranches(false);
    setTitle('');
    setDescription('');
    setDiscount(0);
    setStartDateTime(new Date());
    setEndDateTime(new Date());
    setImageFile(null);
    setCampaignType('');
    setSelectedCompanyId('');
    setSelectedBranchId('');
    setBranches([]);
    setBundlePrice('');
    setBundleTotalCount('');
    setBundleVatRate(null);
    setStatus('');
    setCurrentImageUrl(null);
    setCopyImage(null);
    setSelectedCategoryId('');
    setCategories([]);
  };

  const handleClose = () => {
    resetForm();
    setValidationError(undefined);
    onClose();
  };

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setStartDateTime(date);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      setEndDateTime(date);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!title) {
      setValidationError(t('campaign.title.required'));
      return;
    }
    if (!description) {
      setValidationError(t('campaign.description.required'));
      return;
    }
    if (!campaignType) {
      setValidationError(t('campaign.type.required'));
      return;
    }
    if (!isEditMode && !imageFile) {
      setValidationError(t('campaign.image.required'));
      return;
    }
    if (
      isBundleType &&
      (bundlePrice === '' || (isBundleActivity && bundleTotalCount === ''))
    ) {
      setValidationError(t('campaign.bundle.details.required'));
      return;
    }
    if (campaignType === CampaignType.CATEGORY && selectedCategoryId === '') {
      setValidationError(t('campaign.category.required'));
      return;
    }

    let branchId: number = -1;

    if (
      (isBranchAdmin || ((isCompanyAdmin || isSuperAdmin) && !isAdminView)) &&
      currentBranch?.id
    ) {
      // Branch admin, or a company/super admin working within a specific
      // branch view: the branch selector is hidden there, so derive the
      // branch from currentBranch. SUPER_ADMIN was missing from this arm,
      // which left the branch view with no selector AND no fallback — the
      // save failed with "branch required" and there was no way out in the UI.
      branchId = currentBranch.id;
    } else if (typeof selectedBranchId === 'number') {
      branchId = selectedBranchId;
    } else {
      setValidationError(t('campaign.branch.required'));
      return;
    }

    try {
      setLoading(true);
      await onSave({
        title,
        description,
        discount,
        startDateTime,
        endDateTime,
        type: campaignType as CampaignType,
        imageFile,
        branchId,
        ...(isEditMode && status && { status: status as CampaignStatus }),
        ...(isBundleType && {
          bundlePrice:
            typeof bundlePrice === 'string'
              ? parseFloat(bundlePrice)
              : bundlePrice,
          ...(bundleTotalCount !== '' && { bundleTotalCount }),
          bundleVatRate
        }),
        ...(campaignType === CampaignType.CATEGORY &&
          selectedCategoryId !== '' && {
            categoryId: selectedCategoryId as number
          })
      });
    } catch {
      // The parent owns server error state. Swallow the rejection at the
      // event boundary so a failed save remains recoverable without an
      // unhandled promise rejection in the browser.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} className="campaign-dialog">
      <DialogTitle>
        {isPackageKind
          ? isEditMode
            ? t('edit.package')
            : t('new.package')
          : isEditMode
            ? t('edit.campaign')
            : t('new.campaign')}
        {isEditMode && ` - ${campaign?.title}`}
        {!isEditMode &&
          template &&
          ` - ${t('copy.of', { name: template.title })}`}
      </DialogTitle>
      <DialogContent>
        {!isEditMode && template && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('campaign.copy.hint')}
          </Alert>
        )}
        {copyImage === 'failed' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('copy.image.failed')}
          </Alert>
        )}
        {/* Hata alanı YAPIŞIK: modal içeriği kaydırılmışken hata en üstte
            render edildiği için görüş alanı dışında kalıyor ve "Kaydet hiçbir
            şey yapmıyor" izlenimi veriyordu. */}
        {(error || validationError) && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 3,
              backgroundColor: 'background.paper',
              pt: 1
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {validationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {validationError}
              </Alert>
            )}
          </Box>
        )}
        <TextField
          required
          autoFocus
          margin="dense"
          label={t('title')}
          type="text"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <TextField
          required
          margin="dense"
          label={t('description')}
          type="text"
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {/* Paketin fiyatı paket fiyatıdır; indirim yüzdesi kullanılmaz. */}
        {!isPackageKind && (
          <NumericInput
            required
            margin="dense"
            label={t('discount.percentage')}
            fullWidth
            value={discount}
            onChange={(value) => setDiscount(value)}
            helperText={t('discount.percentage.helper')}
            min={0}
            max={100}
            showEmptyForZero={false}
          />
        )}
        {!isPackageKind && (
          <FormControl fullWidth margin="dense" required>
            <InputLabel>{t('campaign.type')}</InputLabel>
            <Select
              value={campaignType}
              onChange={(e) => setCampaignType(e.target.value as CampaignType)}
              label={t('campaign.type')}
              disabled={isEditMode} // Disable type change in edit mode
            >
              {typeOptions.map((type) => {
                const translationKey = type.toLowerCase().replace('_', '.');
                return (
                  <MenuItem key={type} value={type}>
                    {t(translationKey)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}

        {isEditMode && (
          <FormControl fullWidth margin="dense" required>
            <InputLabel>{t('status')}</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as CampaignStatus)}
              label={t('status')}
            >
              {Object.values(CampaignStatus).map((statusOption) => (
                <MenuItem key={statusOption} value={statusOption}>
                  {t(statusOption.toLowerCase())}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Bundle fields when bundle campaign type is selected */}
        {isBundleType && (
          <>
            <TextField
              required
              margin="dense"
              label={t('bundle.price')}
              type="number"
              fullWidth
              value={bundlePrice}
              onChange={(e) => {
                const value = e.target.value;
                setBundlePrice(value === '' ? '' : Number(value));
              }}
              inputProps={{ min: 0 }}
            />
            {isBundleActivity && (
              <TextField
                required
                margin="dense"
                label={t('bundle.total.count')}
                type="number"
                fullWidth
                value={bundleTotalCount}
                onChange={(e) => {
                  const value = e.target.value;
                  setBundleTotalCount(value === '' ? '' : Number(value));
                }}
                inputProps={{ min: 1 }}
              />
            )}
            <FormControl fullWidth margin="dense">
              <InputLabel id="campaign-bundle-vat-rate-label">
                {t('product.vat.rate')}
              </InputLabel>
              <Select
                labelId="campaign-bundle-vat-rate-label"
                label={t('product.vat.rate')}
                value={bundleVatRate ?? ''}
                onChange={(e) =>
                  setBundleVatRate(
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              >
                <MenuItem value="">
                  <em>{t('product.vat.rate.unspecified')}</em>
                </MenuItem>
                {LEGAL_VAT_RATES.map((rate) => (
                  <MenuItem key={rate} value={rate}>
                    %{rate}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{t('product.vat.rate.helper')}</FormHelperText>
            </FormControl>
          </>
        )}

        {/* Company selection for Super Admin */}
        {isSuperAdmin && (
          <FormControl fullWidth margin="dense" required>
            <InputLabel>{t('company')}</InputLabel>
            <Select
              value={
                typeof selectedCompanyId === 'number' &&
                companies.some((item) => item.id === selectedCompanyId)
                  ? selectedCompanyId
                  : ''
              }
              onChange={(e) => handleCompanyChange(Number(e.target.value))}
              label={t('company')}
              disabled={
                fetchingCompanies || isEditMode || !!branchViewCompanyId
              }
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Branch selection for Super Admin and Company Admin */}
        {(isSuperAdmin || isCompanyAdmin) && isAdminView && (
          <FormControl fullWidth margin="dense" required>
            <InputLabel>{t('branch')}</InputLabel>
            <Select
              value={
                typeof selectedBranchId === 'number' &&
                branches.some((item) => item.id === selectedBranchId)
                  ? selectedBranchId
                  : ''
              }
              onChange={(e) =>
                setSelectedBranchId(
                  typeof e.target.value === 'number'
                    ? e.target.value
                    : Number(e.target.value)
                )
              }
              label={t('branch')}
              disabled={
                fetchingBranches ||
                (isSuperAdmin && !selectedCompanyId) ||
                isEditMode
              }
            >
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Active branch info for branch admin, or a company/super admin
            working within a specific branch view (selector is hidden there) */}
        {(isBranchAdmin ||
          ((isCompanyAdmin || isSuperAdmin) && !isAdminView)) &&
          currentBranch && (
            <TextField
              fullWidth
              margin="dense"
              label={t('branch')}
              value={currentBranch.name}
              disabled
              variant="outlined"
            />
          )}

        {/* Category selection for CATEGORY campaign type */}
        {campaignType === CampaignType.CATEGORY && (
          <FormControl fullWidth margin="dense" required>
            <InputLabel>{t('category')}</InputLabel>
            <Select
              value={
                typeof selectedCategoryId === 'number' &&
                categories.some((item) => item.id === selectedCategoryId)
                  ? selectedCategoryId
                  : ''
              }
              onChange={(e) =>
                setSelectedCategoryId(
                  typeof e.target.value === 'number'
                    ? e.target.value
                    : Number(e.target.value)
                )
              }
              label={t('category')}
              disabled={fetchingCategories}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <StyledDatePicker
            label={t('start.date')}
            selected={startDateTime}
            onChange={handleStartDateChange}
            required
          />

          <StyledDatePicker
            label={t('end.date')}
            selected={endDateTime}
            onChange={handleEndDateChange}
            minDate={startDateTime}
          />
        </Box>
        <FileUploadCard
          onFileSelect={(file) => {
            setImageFile(file);
          }}
          imageUrl={currentImageUrl}
          helperText={
            isEditMode ? undefined : t('campaign.image.required.helper')
          }
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="primary"
          disabled={loading}
          className="campaign-dialog-cancel-button"
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          disabled={loading || copyImage === 'loading'}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? t('saving') : t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CampaignDialog;
